<template>
    <ValidationProvider
        :name="name"
        :rules="rules"
        v-slot="{ errors }"
        tag="div"
        :class="['custom-wrapper', `type${inputType}`, classWrapper]"
        :has-icon="hasIcon"
        :has-shadow="hasShadow"
    >
        <div v-if="showTooltip" class="custom-info">
            <img
                class="ico"
                src="@/assets/images/info-icon.svg"
                alt="info"
                @click="toggleShowTooltip"
            />
            <div
                v-if="isShowTooltip"
                class="custom-tooltip"
                v-click-outside="closeTooltip"
            >
                <div class="custom-tooltip__title">{{ tooltipTitle }}</div>
                <div class="custom-tooltip__desc">
                    {{ tooltipDesc }}
                </div>
                <i
                    class="fa fa-times-circle custom-tooltip__close"
                    aria-hidden="true"
                    @click="closeTooltip"
                ></i>
            </div>
        </div>
        <div
            :class="{
                focus: isFocus,
                disabled: disabled,
                'no-label': !label,
                'has-shadow': hasShadow,
                'custom-form-control-type1': inputType === '1',
                'custom-form-control-type2': inputType === '2',
            }"
            @click="!readonly ? toggleShowMenu(): null"
        >
            <input
                v-bind="$attrs"
                :value="value"
                @input="onInput"
                @focus="onFocus"
                @blur="onBlur"
                :id="name"
                placeholder=""
                hidden
                :readonly="readonly"
            />
            <input
                :value="
                    selected
                        ? typeof selected.show_label !== 'undefined'
                            ? selected.show_label
                            : selected.label
                        : ''
                "
                :placeholder="inputType === '1' ? '' : placeholder"
                readonly
            />
            <label v-if="label">{{ label }}</label>
            <div v-if="!disableIcon" :class="[`custom-form-control-type${inputType}__append-icon`]">
                <slot v-if="hasIcon" name="append-icon" />
                <i v-else class="fa fa-chevron-down" aria-hidden="true"></i>
            </div>
        </div>
        <span :class="[showTooltip && 'show-tooltip']" class="custom-error">{{
            errors[0]
        }}</span>
        <Transition name="slide-fade">
            <div
                v-click-outside="onClickOutside"
                v-if="isShowMenu"
                class="custom-dropdown shadow-sm mt-5"
            >
                <input
                    class="custom-search"
                    placeholder="Search..."
                    type="text"
                    v-model="search"
                    ref="searchInput"
                    v-if="enableSearch"
                    :readonly="readonly"
                />
                <div
                    v-for="option in filterOptions"
                    :key="option.value"
                    class="custom-dropdown__item"
                    @click="() => selectedMenu(option)"
                >
                    {{ option.label }}
                </div>
            </div>
        </Transition>
    </ValidationProvider>
</template>

<script>
export default {
    name: 'SelectField',
    data() {
        return {
            isFocus: false,
            isShowMenu: false,
            selected: null,
            isShowTooltip: false,
            search: '',
            filterOptions: [],
        };
    },
    props: {
        label: {
            type: String,
        },
        value: {
            type: String,
            required: false,
        },
        name: {
            type: String,
            required: true,
        },
        rules: {
            type: [String, Array, Object],
        },
        classWrapper: {
            type: String,
        },
        options: {
            type: Array,
            default: () => [],
        },
        disabled: {
            type: Boolean,
        },
        disableIcon: {
            type: Boolean,
            default: false,
        },
        hasIcon: {
            type: Boolean,
            default: false,
        },
        hasShadow: {
            type: Boolean,
            default: false,
        },
        showTooltip: Boolean,
        tooltipTitle: String,
        tooltipDesc: String,
        enableSearch: {
            type: Boolean,
            default: true,
        },
        inputType: {
            type: String,
            default: '1',
        },
        placeholder: String,
        readonly: Boolean,
    },
    methods: {
        onInput(event) {
            this.$emit('input', event.target.value);
        },
        onFocus(event) {
            this.isFocus = true;
            this.$emit('focus', event.target.value);
        },
        onBlur(event) {
            this.isFocus = false;
            this.$emit('focus', event.target.value);
        },
        toggleShowMenu() {
            if (this.disabled) return;
            this.isShowMenu = !this.isShowMenu;
            if (this.isShowMenu) {
                this.$nextTick(() => {
                    if (this.enableSearch) this.$refs.searchInput.focus();
                });
            }
        },
        selectedMenu(item) {
            this.$emit('input', item.value.toString());
            this.isShowMenu = false;
            this.selected = item;
        },
        onClickOutside() {
            this.isShowMenu = false;
        },
        toggleShowTooltip() {
            this.isShowTooltip = !this.isShowTooltip;
        },
        closeTooltip() {
            this.isShowTooltip = false;
        },
    },
    created() {
        this.selected = this.options.find((item) => item.value === this.value);

        setTimeout(() => {
            this.filterOptions = this.options;
        }, 1000);
    },
    watch: {
        value(value) {
            this.selected = this.options.find((item) => item.value === value);
        },
        search(value) {
            if (value) {
                this.filterOptions = this.options.filter((item) =>
                    item.label.toLowerCase().includes(value.toLowerCase())
                );
            } else {
                this.filterOptions = this.options;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.custom-wrapper {
    position: relative;
    min-height: 90px;
    display: flex;
    flex-wrap: wrap;

    &.none-min-height {
        min-height: 0;
    }
    &.type2 {
        min-height: auto;
    }
}
.custom-dropdown {
    position: absolute;
    left: 0;
    right: 0;
    max-height: 300px;
    background-color: #fff;
    border-radius: 8px;
    z-index: 10;
    overflow-y: auto;
    min-width: 250px;

    &__item {
        padding: 12px 16px;
        cursor: pointer;
        transition: all 0.1s ease-in-out;
        &:hover {
            background-color: #e1e7e9;
        }
    }
}
.custom-form-control-type1 {
    background-color: #e1e7e9;
    padding: 24px 16px 8px 16px;
    border-radius: 12px;
    position: relative;
    height: 59px;
    cursor: pointer;
    box-shadow: 0px 13px 30px -12px rgba(0, 0, 0, 0.25);
    flex-grow: 1;

    &.focus {
        background-color: #e1e7e9;
    }

    &.no-label {
        padding: 16px;
    }

    &.disabled {
        opacity: 0.4;
    }

    &.has-shadow {
        box-shadow: 0px 15px 16px -17px #767676;
    }

    label {
        color: #969fa2;
        font-size: 14px;
        position: absolute;
        margin-bottom: 0;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 2;
        padding: 16px;
        transition: all 0.1s ease-in-out;
        display: flex;
        align-items: center;
    }

    input {
        border: none;
        background: none;
        font-size: 18px;
        color: rgba(51, 48, 60, 0.68);
        padding: 0;
        width: 100%;
        cursor: pointer;

        &::placeholder {
            visibility: hidden;
        }

        &:focus + label,
        &:not(:placeholder-shown) + label {
            font-size: 10px;
            opacity: 0.7;
            padding-block: 0;
            bottom: 24px;
            color: #1b659e;
        }
    }

    &__append-icon {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        cursor: pointer;
        right: 15px;
        z-index: 3;
        display: inline-block;
    }
}

.custom-form-control-type2 {
    width: 100%;
    position: relative;
    height: 40px;
    input {
        border: 1px solid #7c7c7a;
        border-radius: 8px;
        background: none;
        font-size: 16px;
        color: #7c7c7a;
        width: 100%;
        cursor: pointer;
        height: 100%;
        padding-inline: 16px;

        &::placeholder {
            color: #7c7c7a !important;
            opacity: 1;
        }
    }
    &__append-icon {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        cursor: pointer;
        right: 15px;
        z-index: 3;
        display: inline-block;
    }
}
.custom-error {
    color: var(--danger);
    font-size: 12px;
    width: 100%;
    &.show-tooltip {
        margin-left: 30px;
    }
}
.custom-info {
    position: relative;
    .ico {
        color: #7bbdef;
        font-size: 20px;
        cursor: pointer;
        margin-right: 8px;
        margin-top: 20px;
    }
}
.custom-tooltip {
    position: absolute;
    border-radius: 30px;
    background: #edf5f7;
    box-shadow: 0px 13px 30px -12px rgba(0, 0, 0, 0.25);
    min-width: 400px;
    padding: 20px;
    z-index: 10;
    transform: translate(30px, -60px);

    &__title {
        font-size: 16px;
        font-weight: 600;
        line-height: 24px; /* 150% */
        color: #41b8dc;
        margin-bottom: 8px;
    }

    &__desc {
        font-size: 16px;
        line-height: 24px; /* 150% */
        color: #7c7c7a;
    }

    &__close {
        position: absolute;
        right: 16px;
        top: 8px;
        color: #2178ab !important;
        cursor: pointer;
    }
}
.custom-search {
    border: none;
    width: 100%;
    padding: 8px 16px;
    color: #767676;
}
</style>

<style lang="scss">
.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: opacity 0.5s, transform 0.5s;
}
.slide-fade-enter,
.slide-fade-leave-to {
    opacity: 0;
    transform: translateY(-20px);
}
</style>
